import assetsBase from '@exodus/assets-base'
import { UnitType } from '@exodus/currency'
import createFetchival from '@exodus/fetch/create-fetchival'
import {
  ASSET_TYPES,
  FILTER_LIST_MODES,
  ORDER_TYPES,
  PAYMENT_METHOD_TYPES,
  PROVIDERS,
} from '../constants.js'
import { filterByCountries, omitUndefinedValues } from '../utils.js'
import { ERROR_PAYMENT_METHOD_NOT_SUPPORTED } from '../errors.js'
import {
  ENABLED_ASSETS,
  ENABLED_SELL_ASSETS,
  ENABLED_TEST_ASSETS,
  MOONPAY_API_URL,
} from './constants.js'
import moonpayAdapter from './adapter.js'

export * from './constants.js'
export * from './utils.js'
export { default as adapter } from './adapter.js'

const exodusAssets = assetsBase.default || assetsBase
const keyEnvironments = {
  pk_live: 'live',
  pk_test: 'test',
}

const moonpayProvider = ({
  apiKey,
  assets: assets_ = ENABLED_ASSETS,
  countries = {},
  countriesMode = FILTER_LIST_MODES.blacklist,
  enabled = true,
  url = MOONPAY_API_URL,
  fetch: globalFetch = fetch.bind(globalThis), // eslint-disable-line no-undef
  paymentMethodTypes,
} = {}) => {
  if (!apiKey) {
    throw new Error('`apiKey` is required')
  }

  const fetchival = createFetchival({ fetch: globalFetch })
  const fetch_ = fetchival(url)
  const precisions = new Map()
  const assetLimits = new Map()
  const [, environment] =
    Object.entries(keyEnvironments).find(([key]) => apiKey.startsWith(key)) || []

  const isLive = environment === keyEnvironments.pk_live
  const isTest = environment === keyEnvironments.pk_test

  const getAssets = async ({ countryCode, regionCode } = {}) => {
    const availableAssets = []
    const assets = await fetch_('v3/currencies').get({ apiKey })

    for (const asset of assets) {
      const isInvalidType = asset.type !== 'crypto' && asset.type !== 'fiat'
      const isNotAllowedInEnvironment =
        (isLive && asset.supportsLiveMode === false) || (isTest && asset.supportsTestMode === false)

      const isNotAllowedInCountry = asset.notAllowedCountries?.includes(countryCode)
      const isNotAllowedInUsState =
        countryCode === 'US' &&
        (asset.isSupportedInUS === false || asset.notAllowedUSStates?.includes(regionCode))

      if (
        asset.isSuspended ||
        isInvalidType ||
        isNotAllowedInEnvironment ||
        isNotAllowedInCountry ||
        isNotAllowedInUsState
      ) {
        continue
      }

      if (asset.type === 'fiat') {
        const name = asset.code.toUpperCase()

        precisions.set(name, asset.precision)
        assetLimits.set(name, {
          minBuyAmount: asset.minBuyAmount || 0,
          maxBuyAmount: asset.maxBuyAmount || Number.MAX_SAFE_INTEGER,
        })

        availableAssets.push({
          ...assetLimits.get(name),
          isBuySupported: true,
          isSellSupported: Object.values(ENABLED_SELL_ASSETS).includes(asset.code),
          name: asset.code.toUpperCase(),
          type: ASSET_TYPES.fiat,
        })

        continue
      }

      const exodusAsset = exodusAssets[assets_[asset.code]]
      const testAsset = isTest ? exodusAssets[ENABLED_TEST_ASSETS[asset.code]] : undefined
      const name = exodusAsset?.name

      if (!exodusAsset) {
        continue
      }

      precisions.set(name, asset.precision)
      assetLimits.set(name, {
        minSellAmount: asset.minSellAmount || 0,
        minBuyAmount: asset.minBuyAmount || 0,
        maxSellAmount: asset.maxSellAmount || Number.MAX_SAFE_INTEGER,
        maxBuyAmount: asset.maxBuyAmount || Number.MAX_SAFE_INTEGER,
      })

      const availableAsset = {
        ...assetLimits.get(name),
        code: asset.code,
        isBuySupported: true,
        isSellSupported: asset.isSellSupported,
        name,
        type: ASSET_TYPES.crypto,
      }

      availableAssets.push(availableAsset)

      if (testAsset) {
        precisions.set(testAsset.name, precisions.get(name))
        assetLimits.set(testAsset.name, assetLimits.get(name))
        availableAssets.push({ ...availableAsset, name: testAsset.name })
      }
    }

    if (availableAssets.length === 0) {
      return availableAssets
    }

    return availableAssets
  }

  const getCountries = async ({ countryCode, regionCode } = {}) => {
    const countries_ = await fetch_('v3/countries').get({ apiKey })
    const formattedCountries = []

    for (const { alpha2, name, isAllowed, isBuyAllowed, isSellAllowed, states } of countries_) {
      const hasStates = Array.isArray(states)
      const state = states?.find((state) => state.code === regionCode)
      const filterByTypeLocation = (type) =>
        filterByCountries(countries, state ? [alpha2, state.code] : alpha2, {
          mode: countriesMode,
          properties: [type],
        })

      if (!isAllowed || (!isBuyAllowed && !isSellAllowed)) {
        continue
      }

      const isBuySupported =
        isBuyAllowed &&
        (!countryCode || !regionCode || !hasStates || state?.isBuyAllowed) &&
        !filterByTypeLocation(ORDER_TYPES.buy)

      const isSellSupported =
        isSellAllowed &&
        (!countryCode || !regionCode || !hasStates || state?.isSellAllowed) &&
        !filterByTypeLocation(ORDER_TYPES.sell)

      if (!isBuySupported && !isSellSupported) {
        continue
      }

      formattedCountries.push({
        code: alpha2,
        isBuySupported,
        isSellSupported,
        name,
      })
    }

    return formattedCountries
  }

  const getOrder = async (id, { serverOrder, type = ORDER_TYPES.buy } = Object.create(null)) => {
    const encodedId = encodeURIComponent(id)
    const endpoint = type === ORDER_TYPES.buy ? 'v1/transactions' : 'v3/sell_transactions'
    const order = await fetch_(`${endpoint}/${encodedId}`).get({ apiKey })

    return moonpayAdapter.fromProviderToExodusOrder(
      {
        ...order,
        id,
        type,
        toAmountInitial: serverOrder?.toAmountInitial,
      },
      assets_
    )
  }

  const getQuote = async ({
    countryCode,
    fromAmount,
    fromAsset,
    paymentMethod = PAYMENT_METHOD_TYPES.card,
    regionCode,
    toAmount,
    toAsset,
    type = ORDER_TYPES.buy,
  } = {}) => {
    if (!ORDER_TYPES[type]) {
      throw new Error(`Invalid order type "${type}"`)
    }

    const isBuyType = type === ORDER_TYPES.buy
    const cryptoAmount = isBuyType ? toAmount : fromAmount
    const cryptoAsset = isBuyType ? toAsset : fromAsset
    const providerPaymentMethod = moonpayAdapter.fromExodusToProviderPaymentMethod(paymentMethod, {
      assets: assets_,
      country: countryCode,
      currency: isBuyType ? fromAsset : toAsset,
      orderType: type,
      paymentMethodTypes,
    })

    if (!providerPaymentMethod) {
      throw new Error(ERROR_PAYMENT_METHOD_NOT_SUPPORTED.message)
    }

    if (typeof cryptoAmount === 'number' && precisions.size === 0) {
      await getAssets({ countryCode, regionCode })
    }

    const { currency: cryptoCurrency } = exodusAssets[cryptoAsset]
    const ticker = moonpayAdapter.fromExodusToProviderAsset(cryptoAsset, assets_)
    const encodedTicker = encodeURIComponent(ticker)
    const unit = UnitType.create({
      base: 0,
      [cryptoAsset]: cryptoCurrency.defaultUnit.power,
    })

    const fromAssetCode = isBuyType ? fromAsset?.toLowerCase() : encodedTicker
    const toAssetCode = isBuyType ? encodedTicker : toAsset?.toLowerCase()
    const cryptoAmountFormatted =
      typeof cryptoAmount === 'number'
        ? unit.defaultUnit(cryptoAmount).toDefaultNumber()
        : cryptoAmount

    const parameters = omitUndefinedValues({
      apiKey,
      areFeesIncluded: true,
      baseCurrencyAmount: isBuyType ? fromAmount : cryptoAmountFormatted,
      baseCurrencyCode: fromAssetCode,
      quoteCurrencyAmount: isBuyType ? cryptoAmountFormatted : toAmount,
      quoteCurrencyCode: toAssetCode,
    })

    const [{ value: quote, reason: quoteError }, { value: limits }] = await Promise.allSettled([
      fetch_(`v3/currencies/${encodedTicker}/${isBuyType ? 'buy' : 'sell'}_quote`).get({
        ...parameters,
        ...(isBuyType
          ? { paymentMethod: providerPaymentMethod }
          : { payoutMethod: providerPaymentMethod }),
      }),
      isBuyType
        ? fetch_(`v3/currencies/${encodedTicker}/limits`).get({
            ...parameters,
            paymentMethod: providerPaymentMethod,
          })
        : { baseCurrency: assetLimits.get(fromAsset), quoteCurrency: assetLimits.get(toAsset) },
    ])

    if (quoteError) {
      if (quoteError.response?.status !== 400) {
        throw quoteError
      }

      const quoteErrorBody = await quoteError.response.json()

      if (!/minimum .* amount/iu.test(quoteErrorBody?.message)) {
        throw quoteError
      }
    }

    return moonpayAdapter.fromProviderToExodusQuote({
      ...quote,
      fromAsset,
      fromMaxBuyAmount: limits.baseCurrency?.maxBuyAmount,
      fromMaxSellAmount: limits.baseCurrency?.maxSellAmount,
      fromMinBuyAmount: limits.baseCurrency?.minBuyAmount,
      fromMinSellAmount: limits.baseCurrency?.minSellAmount,
      paymentMethod: providerPaymentMethod,
      toAsset,
      toMaxBuyAmount: limits.quoteCurrency?.maxBuyAmount,
      toMaxSellAmount: limits.quoteCurrency?.minSellAmount,
      toMinBuyAmount: limits.quoteCurrency?.minBuyAmount,
      toMinSellAmount: limits.quoteCurrency?.minSellAmount,
      type,
    })
  }

  const getPaymentMethod = (paymentMethod, parameters = Object.create(null)) => {
    const isTest = environment === keyEnvironments.pk_test
    const assets = {
      __proto__: null,
      ...assets_,
      ...(isTest &&
        Object.fromEntries(
          // Prevent overriding existing keys by adding a suffix. The keys are not used for fitering.
          Object.entries(ENABLED_TEST_ASSETS).map(([key, value]) => [`${key}_test`, value])
        )),
    }

    return moonpayAdapter.fromExodusToProviderPaymentMethod(paymentMethod, {
      ...parameters,
      assets,
      paymentMethodTypes,
    })
  }

  return {
    name: PROVIDERS.moonpay,
    enabled,
    getAssets,
    getCountries,
    getOrder,
    getQuote,
    getPaymentMethod,
    orderTypes: [ORDER_TYPES.buy, ORDER_TYPES.sell],
  }
}

export default moonpayProvider
